/**
 * Admin Orders Page
 * View and manage active orders with QR code printing
 */

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Loader2, Printer, RefreshCw, Camera, Banknote, CreditCard,
  Tag, Clock, Users, AlertCircle, Minus, Plus, ExternalLink,
  ChevronUp, ChevronDown, Video, Play, RotateCcw, Grid3x3,
} from 'lucide-react';
import { deskService, type DeskOrder, type LineItem, type MemoryInfo } from '@/services/deskService';
import { adminRestaurantService } from '@/services/adminRestaurantService';
import { buildOrderUrl } from '@/lib/url';
import { formatPrice } from '@/lib/demoPrice';
import { OnboardingDrawer, type OnboardingItemData } from '@/components/admin/drawers/OnboardingDrawer';
import { useAnalytics } from '@/hooks/useAnalytics';
import { AdminPageShell, PageHeader, PageLoading, AdminButton } from '@/components/admin/shell';
import { printTickets } from '@/services/ticketPrinterService';
import { OrderQRStatus } from '@/components/admin/orders/OrderQRStatus';

const QR_CODE_SIZE = 400;
const MAX_ITEMS_EXPANDED = 5;

interface QRCodeCache {
  [orderId: string]: string;
}

interface ExpandedState {
  [orderId: string]: boolean;
}

interface OrderQuantity {
  [orderId: string]: number;
}

type ShiftFilter = 'current' | 'lunch' | 'dinner' | 'all';

const generateQrUrl = (providerOrderId: string): string =>
  buildOrderUrl(providerOrderId);


/**
 * Get card brand icon with color
 */
const getCardBrandIcon = (brand: string | null): JSX.Element | null => {
  if (!brand) return null;
  const brandLower = brand.toLowerCase();

  const colors: Record<string, string> = {
    visa: 'text-blue-600',
    mastercard: 'text-orange-600',
    amex: 'text-green-600',
    american_express: 'text-green-600',
  };

  return (
    <span className={`font-medium ${colors[brandLower] || 'text-gray-600'}`}>
      {brand}
    </span>
  );
};

/**
 * Format date for display
 */
const formatDateTime = (dateString: string | null): string => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  const today = new Date();
  const isToday =
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();

  if (isToday) {
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } else {
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
};

/**
 * Payment icon component
 */
const PaymentIcon = ({ order }: { order: DeskOrder }) => {
  if (order.identification.payment_type === 'CASH') {
    return <Banknote className="w-4 h-4 text-green-600" />;
  }
  return <CreditCard className="w-4 h-4 text-blue-600" />;
};

/**
 * Order state badge component
 * Shows "En curso" badge for OPEN orders, nothing for COMPLETED
 */
const OrderStateBadge = ({ state }: { state: string }): JSX.Element | null => {
  if (state === 'OPEN') {
    return (
      <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-sm font-medium px-3 py-1">
        En curso
      </Badge>
    );
  }
  return null;
};

type VideoStatus = MemoryInfo['status'];

const getVideoStatusConfig = (status: VideoStatus, progress: number | null) => {
  switch (status) {
    case 'done':
      return { label: 'Generado', color: 'bg-green-100 text-green-800 border-green-300', icon: null };
    case 'rendering':
      return {
        label: `Generando ${progress ?? 0}%`,
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: <Loader2 className="w-3 h-3 animate-spin" />
      };
    case 'failed':
      return { label: 'Error', color: 'bg-red-100 text-red-800 border-red-300', icon: null };
    case 'queued':
    case 'draft':
      return { label: 'Pendiente', color: 'bg-gray-100 text-gray-800 border-gray-300', icon: null };
    default:
      return { label: 'Sin video', color: 'bg-gray-100 text-gray-500 border-gray-200', icon: null };
  }
};

const VideoStatusBadge = ({ status, progress }: { status: VideoStatus; progress: number | null }): JSX.Element => {
  const config = getVideoStatusConfig(status, progress);
  return (
    <Badge variant="outline" className={`${config.color} text-xs font-medium px-2 py-0.5 flex items-center gap-1`}>
      {config.icon}
      {config.label}
    </Badge>
  );
};

export function OrdersPage() {
  const navigate = useNavigate();
  const { trackAdminOrder } = useAnalytics();
  const [qrCodes, setQrCodes] = useState<QRCodeCache>({});
  const [printing, setPrinting] = useState<string | null>(null);
  const isPrintingRef = useRef<string | null>(null);
  const [expandedOrders, setExpandedOrders] = useState<ExpandedState>({});
  const [onboardingDrawerOpen, setOnboardingDrawerOpen] = useState(false);
  const [selectedItemForOnboarding, setSelectedItemForOnboarding] = useState<OnboardingItemData | null>(null);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [orderToRegenerate, setOrderToRegenerate] = useState<DeskOrder | null>(null);
  const [regenerating, setRegenerating] = useState(false);
  const [mobileSelectedOrder, setMobileSelectedOrder] = useState<DeskOrder | null>(null);
  const [shiftFilter, setShiftFilter] = useState<ShiftFilter>('current');
  const [quantities, setQuantities] = useState<OrderQuantity>({});

  const getQuantity = (orderId: string): number => quantities[orderId] || 1;

  const incrementQuantity = (orderId: string) => {
    setQuantities((prev) => ({
      ...prev,
      [orderId]: Math.min((prev[orderId] || 1) + 1, 10),
    }));
  };

  const decrementQuantity = (orderId: string) => {
    setQuantities((prev) => ({
      ...prev,
      [orderId]: Math.max((prev[orderId] || 1) - 1, 1),
    }));
  };

  const { data: adminRestaurant } = useQuery({
    queryKey: ['admin-restaurant'],
    queryFn: () => adminRestaurantService.getRestaurant(),
  });

  const {
    data: ordersData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['admin-orders', shiftFilter],
    queryFn: () => deskService.getOrders(shiftFilter),
    refetchInterval: 5000,
  });

  const orders = useMemo(() => ordersData?.orders || [], [ordersData]);
  const currentShift = ordersData?.current_shift;

  // Poll sync status while sync is in progress
  const { data: syncStatus } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => deskService.getSyncStatus(),
    enabled: syncInProgress,
    refetchInterval: 2000, // Poll every 2 seconds
  });

  // Manual sync mutation
  const syncMutation = useMutation({
    mutationFn: () => deskService.syncOrders(),
    onSuccess: () => {
      setSyncInProgress(true);
      toast.loading('Sincronización iniciada...', { id: 'sync' });
    },
    onError: () => {
      toast.error('Error al iniciar sincronización');
    },
  });

  // Monitor sync status and update UI
  useEffect(() => {
    if (!syncStatus) return;

    if (syncStatus.status === 'running') {
      const synced = syncStatus.synced || 0;
      const total = syncStatus.total || 0;
      toast.loading(
        `Sincronizando: ${synced}/${total} órdenes`,
        { id: 'sync' }
      );
    } else if (syncStatus.status === 'completed') {
      const synced = syncStatus.synced || 0;
      // Replace loading toast with success using same id
      toast.success(
        `Sincronización completada: ${synced} órdenes`,
        { id: 'sync', duration: 4000 }
      );
      setSyncInProgress(false);
      refetch(); // Refetch orders now that sync is done
    } else if (syncStatus.status === 'failed') {
      // Replace loading toast with error using same id
      toast.error('Error en la sincronización', { id: 'sync' });
      setSyncInProgress(false);
    }
  }, [syncStatus, refetch]);

  /**
   * Toggle expanded state for an order
   */
  const toggleExpanded = (orderId: string) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  const handleOpenMobileOrderDetail = (order: DeskOrder) => {
    if (typeof window !== 'undefined' && window.innerWidth >= 768) return;
    setMobileSelectedOrder(order);
  };

  /**
   * Count onboarded items in an order
   */
  const countOnboardedItems = (order: DeskOrder): number => {
    return order.line_items?.filter((item) => item.onboarded).length || 0;
  };

  /**
   * Count non-onboarded items in an order
   */
  const countNotOnboardedItems = (order: DeskOrder): number => {
    return order.line_items?.filter((item) => !item.onboarded).length || 0;
  };

  /**
   * Handle click on non-onboarded item to start onboarding
   */
  const handleItemOnboarding = (item: LineItem) => {
    if (item.onboarded) return; // Already onboarded, do nothing

    // Dish already in Kombu (e.g. added from the TPV catalog) but without photo:
    // complete it in its edit page instead of creating a duplicate.
    if (item.item_id) {
      navigate(`/admin/items/${item.item_id}/edit`);
      return;
    }

    const itemData: OnboardingItemData = {
      remote_item_id: item.remote_item_id || undefined,
      name: item.name,
      price_cents: item.total_money?.amount ? Math.round(item.total_money.amount / (item.quantity || 1)) : undefined,
      currency: item.total_money?.currency || 'EUR',
    };

    setSelectedItemForOnboarding(itemData);
    setOnboardingDrawerOpen(true);
  };

  /**
   * Generate QR codes for orders
   */
  const generateQrCodesForOrders = useCallback(
    async (orderList: DeskOrder[]) => {
      for (const order of orderList) {
        if (qrCodes[order.id]) continue;

        try {
          const qrUrl = generateQrUrl(order.provider_order_id);
          const qrDataUrl = await QRCode.toDataURL(qrUrl, {
            width: QR_CODE_SIZE,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF',
            },
          });

          setQrCodes((prev) => ({ ...prev, [order.id]: qrDataUrl }));
        } catch (err) {
          console.error(`Error generating QR for order ${order.id}:`, err);
        }
      }
    },
    [qrCodes]
  );

  /**
   * Handle print QR code
   */
  const handlePrint = async (order: DeskOrder) => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = order.id;

    const onboardedCount = countOnboardedItems(order);
    if (onboardedCount === 0) {
      toast.error('Esta orden no tiene items con foto. Por favor, añade fotos a los items antes de imprimir el QR.');
      isPrintingRef.current = null;
      return;
    }

    const count = getQuantity(order.id);
    setPrinting(order.id);

    try {
      await deskService.printOrder(order.id, count);

      if (!qrCodes[order.id]) {
        toast.error('El código QR aún no está listo. Espera un momento.');
        return;
      }

      // Increment print count in backend
      await deskService.printOrder(order.id, count);

      // Print physically
      const result = await printTickets(
        { qrCodeUrl: qrCodes[order.id], logoUrl: adminRestaurant?.logo_url },
        {
          count,
          onProgress: (current, total) => {
            toast.info(`Imprimiendo ticket ${current}/${total}...`);
          }
        }
      );

      trackAdminOrder.printBrowser(order.provider_order_id);

      if (result.failed > 0) {
        toast.error(`Error al imprimir ${result.failed} ticket(s). ${result.success} impresos correctamente.`);
      } else {
        toast.success(`${count} QR${count > 1 ? 's' : ''} enviado${count > 1 ? 's' : ''} a la impresora`);
      }
    } catch (err) {
      console.error('Print error:', err);
      toast.error('Error al imprimir. Por favor, verifica la impresora.');
    } finally {
      setPrinting(null);
      isPrintingRef.current = null;
    }
  };

  /**
   * Handle regenerate video click - opens confirmation dialog
   */
  const handleRegenerateVideoClick = (order: DeskOrder) => {
    setOrderToRegenerate(order);
    setRegenerateDialogOpen(true);
  };

  /**
   * Confirm and execute video regeneration
   */
  const confirmRegenerateVideo = async () => {
    if (!orderToRegenerate) return;

    setRegenerating(true);
    try {
      await deskService.regenerateVideo(orderToRegenerate.id);
      toast.success('Regeneración de video iniciada');
      setRegenerateDialogOpen(false);
      setOrderToRegenerate(null);
      refetch();
    } catch (error) {
      const apiError = error as { response?: { data?: { error?: string } } };
      const errorMsg = apiError?.response?.data?.error || 'Error al regenerar el video';
      toast.error(errorMsg);
    } finally {
      setRegenerating(false);
    }
  };



  // Generate QR codes when orders change
  useEffect(() => {
    if (orders.length > 0) {
      generateQrCodesForOrders(orders);
    }
  }, [orders, generateQrCodesForOrders]);

  if (isLoading && orders.length === 0) {
    return (
      <AdminPageShell>
        <div className="space-y-6">
          <PageHeader title="Órdenes Activas" />
          <PageLoading variant="list" rows={3} />
        </div>
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader
          title="Órdenes Activas"
          subtitle={`${orders.length} ${orders.length === 1 ? 'orden' : 'órdenes'} recientes${currentShift && currentShift !== 'unknown' ? ` (${currentShift === 'lunch' ? 'Comida' : 'Cena'})` : ''}`}
        >
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <AdminButton
              onClick={() => navigate('/admin/orders')}
              variant="ghost"
              className="min-h-11"
              title="Vista simple"
            >
              <Grid3x3 className="h-4 w-4" />
            </AdminButton>
            <select
              value={shiftFilter}
              onChange={(e) => setShiftFilter(e.target.value as ShiftFilter)}
              className="h-11 px-3 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="current">Turno actual</option>
              <option value="lunch">Comida</option>
              <option value="dinner">Cena</option>
              <option value="all">Todos</option>
            </select>
            <AdminButton
              onClick={() => syncMutation.mutate()}
              isLoading={syncMutation.isPending || isRefetching}
              loadingText="Sincronizando..."
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${syncMutation.isPending || isRefetching ? 'animate-spin' : ''}`} />
              Actualizar
            </AdminButton>
          </div>
        </PageHeader>

        <div className="space-y-4">
          {orders.map((order) => {
            const onboardedCount = countOnboardedItems(order);
            const totalItems = order.line_items?.length || 0;
            const isExpanded = expandedOrders[order.id] || false;
            const itemsToShow = isExpanded
              ? order.line_items
              : order.line_items?.slice(0, MAX_ITEMS_EXPANDED);
            const hasMoreItems = totalItems > MAX_ITEMS_EXPANDED;
            return (
              <div
                key={order.id}
                className="bg-white border border-gray-200 rounded hover:shadow-sm transition-shadow cursor-pointer md:cursor-default"
                onClick={() => handleOpenMobileOrderDetail(order)}
              >
                <div className="p-4 md:p-6">
                  {/* Mobile Layout */}
                  <div className="md:hidden space-y-3">
                    {/* Header row: Order ID + Badges + Total */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap flex-1">
                        <h3 className="text-lg font-light text-content-primary">
                          #{order.provider_order_id.slice(-6)}
                        </h3>
                        {order.identification.ticket_name && (
                          <Badge className="text-sm font-semibold px-3 py-1 bg-content-primary text-white shadow-sm">
                            <Tag className="w-3 h-3 mr-1" />
                            {order.identification.ticket_name}
                          </Badge>
                        )}
                        <OrderStateBadge state={order.state} />
                      </div>
                      {order.total_money && (
                        <span className="text-lg font-light text-content-primary whitespace-nowrap">
                          {formatPrice(order.total_money.amount, order.total_money.currency)}
                        </span>
                      )}
                    </div>
                    
                    {/* Status + Metadata */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <OrderQRStatus order={order} />
                      {order.created_at && (
                        <span className="text-xs font-light text-content-secondary flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDateTime(order.created_at)}
                        </span>
                      )}
                      {order.identification.guests_count && (
                        <span className="text-xs font-light text-content-secondary flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {order.identification.guests_count}
                        </span>
                      )}
                    </div>
                    
                    {/* Items count */}
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                      {totalItems} {totalItems === 1 ? 'item' : 'items'}
                      {onboardedCount > 0 && (
                        <span className="text-green-600">
                          ({onboardedCount} <Camera className="w-3 h-3 inline" />)
                        </span>
                      )}
                      {countNotOnboardedItems(order) > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 uppercase tracking-wider px-2 py-1 rounded">
                          <AlertCircle className="w-3 h-3" />
                          {countNotOnboardedItems(order)} sin foto
                        </span>
                      )}
                    </div>
                    
                    {/* QR + Print button row */}
                    <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                      <div className="flex-shrink-0">
                        {qrCodes[order.id] ? (
                          <img
                            src={qrCodes[order.id]}
                            alt={`QR para orden ${order.provider_order_id}`}
                            className="w-14 h-14"
                          />
                        ) : (
                          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 border rounded">
                            <RefreshCw className="animate-spin h-4 w-4 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 w-9 p-0"
                            onClick={(e) => { e.stopPropagation(); decrementQuantity(order.id); }}
                            disabled={getQuantity(order.id) <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-6 text-center text-sm font-medium">{getQuantity(order.id)}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 w-9 p-0"
                            onClick={(e) => { e.stopPropagation(); incrementQuantity(order.id); }}
                            disabled={getQuantity(order.id) >= 10}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                         <Button
                           size="sm"
                           onClick={(e) => {
                             e.stopPropagation();
                             handlePrint(order);
                           }}
                           disabled={printing === order.id || !qrCodes[order.id] || onboardedCount === 0}
                           className={`min-h-11 ${!order.memory ? 'bg-[#1e1e1e] text-white hover:bg-[#333]' : ''}`}
                         >
                           <Printer className="mr-2 h-4 w-4" />
                           {printing === order.id ? '...' : 'Imprimir'}
                         </Button>
                      </div>
                    </div>
                  </div>
                  
                  {/* Desktop Layout */}
                  <div className="hidden md:flex gap-6">
                    {/* QR Code */}
                    <div className="flex-shrink-0">
                      {qrCodes[order.id] ? (
                        <a
                          href={generateQrUrl(order.provider_order_id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block bg-white p-2 hover:bg-gray-50 transition-colors border rounded"
                        >
                          <img
                            src={qrCodes[order.id]}
                            alt={`QR para orden ${order.provider_order_id}`}
                            className="w-24 h-24"
                          />
                        </a>
                      ) : (
                        <div className="w-24 h-24 flex items-center justify-center bg-gray-100 border rounded">
                          <RefreshCw className="animate-spin h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Order Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-xl font-light text-content-primary">
                            #{order.provider_order_id.slice(-6)}
                          </h3>
                        {order.identification.ticket_name && (
                          <Badge className="text-base font-semibold px-4 py-1.5 bg-content-primary text-white shadow-sm">
                            <Tag className="w-4 h-4 mr-2" />
                            {order.identification.ticket_name}
                          </Badge>
                        )}
                          <OrderStateBadge state={order.state} />
                          <OrderQRStatus order={order} />
                        </div>
                        {order.total_money && (
                          <span className="text-xl font-light text-content-primary">
                            {formatPrice(order.total_money.amount, order.total_money.currency)}
                          </span>
                        )}
                      </div>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs font-light text-content-secondary mb-4 flex-wrap">
                        {order.created_at && (
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatDateTime(order.created_at)}
                          </div>
                        )}
                        {order.identification.guests_count && (
                          <div className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {order.identification.guests_count}{' '}
                            {order.identification.guests_count === 1 ? 'comensal' : 'comensales'}
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <PaymentIcon order={order} />
                          {order.identification.payment_type === 'CASH' ? (
                            <span>Efectivo</span>
                          ) : (
                            <span>
                              {order.identification.card_brand && getCardBrandIcon(order.identification.card_brand)}
                              {order.identification.card_last_4 && ` ****${order.identification.card_last_4}`}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Items */}
                      <div>
                        <div className="flex items-center gap-2 text-sm font-medium mb-2 text-gray-700">
                          {totalItems} {totalItems === 1 ? 'item' : 'items'}
                          {onboardedCount > 0 && (
                            <span className="text-green-600">
                              ({onboardedCount} <Camera className="w-3 h-3 inline" />)
                            </span>
                          )}
                          {countNotOnboardedItems(order) > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 uppercase tracking-wider px-2 py-1 rounded">
                              <AlertCircle className="w-3 h-3" />
                              {countNotOnboardedItems(order)} sin foto
                            </span>
                          )}
                        </div>

                        {itemsToShow && itemsToShow.length > 0 && (
                          <div className="space-y-1 text-sm">
                            {itemsToShow.map((item, idx) => (
                              <div
                                key={idx}
                                className={`flex items-center justify-between gap-2 p-2 rounded transition-colors ${!item.onboarded
                                  ? 'hover:bg-orange-50 cursor-pointer border border-transparent hover:border-orange-200'
                                  : ''
                                  }`}
                                onClick={() => !item.onboarded && handleItemOnboarding(item)}
                                title={!item.onboarded ? 'Click para onboardear este item' : ''}
                              >
                                <div className="flex items-center gap-2 flex-1">
                                  {item.onboarded ? (
                                    <Camera className="w-4 h-4 text-green-600 flex-shrink-0" />
                                  ) : (
                                    <AlertCircle className="w-4 h-4 text-orange-600 flex-shrink-0" />
                                  )}
                                  <span className={item.onboarded ? 'font-medium' : 'text-gray-700'}>
                                    {item.name}
                                  </span>
                                  {item.onboarded && item.remote_item_id && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/admin/items/${item.remote_item_id}/edit?lookup=remote`);
                                      }}
                                      className="text-green-600 hover:text-green-700 p-1 hover:bg-green-50 rounded transition-colors"
                                      title="Ver/editar item en catálogo"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  {!item.onboarded && (
                                    <span className="text-xs text-orange-600 font-medium">
                                      (click para añadir foto)
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 text-gray-600">
                                  <span>x{item.quantity}</span>
                                  {item.total_money && (
                                    <span className="font-medium">
                                      {formatPrice(item.total_money.amount, item.total_money.currency)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {hasMoreItems && (
                          <Button
                            size="sm"
                            onClick={() => toggleExpanded(order.id)}
                            className="mt-2 bg-transparent text-content-primary border-none hover:bg-gray-100 font-light text-xs"
                          >
                            {isExpanded ? (
                              <>
                                Ver menos <ChevronUp className="ml-2 h-4 w-4" />
                              </>
                            ) : (
                              <>
                                Ver {totalItems - MAX_ITEMS_EXPANDED} más{' '}
                                <ChevronDown className="ml-2 h-4 w-4" />
                              </>
                            )}
                          </Button>
                        )}
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="md:hidden min-h-11 text-xs mb-3"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenMobileOrderDetail(order);
                        }}
                      >
                        Ver detalles
                      </Button>

                      {/* Video Section */}
                      {order.memory && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-3 mb-3">
                            <Video className="w-4 h-4 text-gray-600" />
                            <span className="text-sm font-medium text-gray-700">Video</span>
                            <VideoStatusBadge status={order.memory.status} progress={order.memory.progress} />
                          </div>

                          {order.memory.status === 'done' && order.memory.video_url && (
                            <div className="flex items-start gap-4">
                              <div className="relative group">
                                <video
                                  src={order.memory.video_url}
                                  className="w-20 h-36 rounded object-cover bg-gray-100"
                                  preload="metadata"
                                  controls
                                />
                                <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:opacity-0 transition-opacity pointer-events-none">
                                  <Play className="w-6 h-6 text-white" />
                                </div>
                              </div>
                              <div className="flex-1 space-y-1">
                                <p className="text-xs text-gray-600">
                                  <span className="font-medium">Generado:</span>{' '}
                                  {formatDateTime(order.memory.video_generated_at)}
                                </p>
                                <p className="text-xs text-gray-600">
                                  <span className="font-medium">Vistas:</span>{' '}
                                  {order.memory.video_views_count}
                                </p>
                              </div>
                            </div>
                          )}

                          {order.memory.status === 'rendering' && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generando video... {order.memory.progress ?? 0}%
                            </div>
                          )}

                          {order.memory.status === 'failed' && order.memory.error_message && (
                            <div className="text-xs text-red-600 bg-red-50 p-2 rounded mb-2">
                              Error: {order.memory.error_message}
                            </div>
                          )}

                          {order.memory.status !== 'rendering' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRegenerateVideoClick(order)}
                              className="mt-2 text-xs h-7"
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Regenerar video
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 w-9 p-0"
                            onClick={() => decrementQuantity(order.id)}
                            disabled={getQuantity(order.id) <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-6 text-center text-sm font-medium">{getQuantity(order.id)}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-9 w-9 p-0"
                            onClick={() => incrementQuantity(order.id)}
                            disabled={getQuantity(order.id) >= 10}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <Button
                          onClick={() => handlePrint(order)}
                          disabled={printing === order.id || !qrCodes[order.id] || onboardedCount === 0}
                          className="bg-content-primary text-white border border-content-primary hover:bg-white hover:text-content-primary transition-all font-light disabled:opacity-50 disabled:cursor-not-allowed"
                          title={onboardedCount === 0 ? 'Añade fotos a los items antes de imprimir' : ''}
                        >
                          <Printer className="mr-2 h-4 w-4" />
                          {printing === order.id ? '...' : 'Imprimir'}
                        </Button>
                      </div>
                      {onboardedCount === 0 && (
                        <p className="text-xs text-orange-600 font-medium">Añade fotos primero</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {orders.length === 0 && !isLoading && (
          <div className="bg-white border border-gray-200 rounded p-12 text-center">
            <p className="text-sm font-light text-content-secondary">No hay órdenes activas</p>
            <p className="text-xs font-light text-content-secondary mt-2">
              Las órdenes aparecerán aquí cuando se completen en el TPV
            </p>
          </div>
        )}


        <Sheet open={mobileSelectedOrder !== null} onOpenChange={(open) => !open && setMobileSelectedOrder(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
            {mobileSelectedOrder && (
              <>
                <SheetHeader>
                  <SheetTitle>Orden #{mobileSelectedOrder.provider_order_id.slice(-6)}</SheetTitle>
                  <SheetDescription>Detalles completos de la orden seleccionada.</SheetDescription>
                </SheetHeader>

                <div className="mt-6 space-y-4">
                  <div className="rounded-lg border p-3">
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-lg font-medium text-content-primary">
                      {mobileSelectedOrder.total_money
                        ? formatPrice(mobileSelectedOrder.total_money.amount, mobileSelectedOrder.total_money.currency)
                        : '-'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-sm font-medium text-content-primary">Items ({mobileSelectedOrder.line_items?.length || 0})</p>
                    <div className="space-y-2">
                      {mobileSelectedOrder.line_items?.map((item) => (
                        <div key={`${mobileSelectedOrder.id}-${item.uid}`} className="rounded border border-gray-200 px-3 py-2 text-sm">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-gray-800">{item.name}</span>
                            <span className="text-gray-600">x{item.quantity}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Onboarding Drawer */}
        <OnboardingDrawer
          open={onboardingDrawerOpen}
          onOpenChange={setOnboardingDrawerOpen}
          itemData={selectedItemForOnboarding}
        />

        {/* Regenerate Video Confirmation Dialog */}
        <Dialog open={regenerateDialogOpen} onOpenChange={setRegenerateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-light text-content-primary">
                ¿Regenerar video?
              </DialogTitle>
              <DialogDescription className="text-sm text-content-secondary">
                Se eliminará el video actual y se generará uno nuevo.
                Este proceso puede tardar varios segundos.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setRegenerateDialogOpen(false)}
                disabled={regenerating}
                className="border-content-primary text-content-primary hover:bg-gray-100 font-light"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmRegenerateVideo}
                disabled={regenerating}
                className="bg-content-primary text-white hover:bg-content-primary/90 font-light"
              >
                {regenerating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Regenerando...
                  </>
                ) : (
                  <>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Regenerar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminPageShell>
  );
}
